/**
 * Predefined color palette for family members
 * Colors are designed to be visually distinct and accessible
 */

export type ColorOption = {
  hex: string;
  name: string;
};

/**
 * Predefined colors for family members
 * Order matters - first available color is used for "Me"
 */
export const FAMILY_MEMBER_COLORS: ColorOption[] = [
  { hex: '#ffaf3f', name: 'Orange' },
  { hex: '#2296f3', name: 'Blue' },
  { hex: '#fff48d', name: 'Yellow' },
  { hex: '#ff6e68', name: 'Red' },
  { hex: '#aaff60', name: 'Green' },
  { hex: '#cc3da9', name: 'Purple' },
  { hex: '#4dd0e1', name: 'Teal' },
  { hex: '#ff8a80', name: 'Pink' },
  { hex: '#c6ff00', name: 'Lime' },
  { hex: '#7c4dff', name: 'Indigo' },
];

export class FamilyMemberColorPalette {
  /**
   * Get all available colors
   */
  static getAllColors(): ColorOption[] {
    return [...FAMILY_MEMBER_COLORS];
  }

  /**
   * Get the first available color not in the used list
   */
  static getNextAvailableColor(usedColors: string[]): ColorOption | null {
    const usedLower = usedColors.filter(Boolean).map(c => c.toLowerCase());
    return FAMILY_MEMBER_COLORS.find(
      color => !usedLower.includes(color.hex.toLowerCase())
    ) || null;
  }

  /**
   * Check if a color is a valid hex color (any 7-char hex string)
   */
  static isValidColor(hex: string): boolean {
    return /^#[0-9A-Fa-f]{6}$/.test(hex);
  }

  /**
   * Check if a color is in the predefined palette
   */
  static isPaletteColor(hex: string): boolean {
    return FAMILY_MEMBER_COLORS.some(
      color => color.hex.toLowerCase() === hex.toLowerCase()
    );
  }

  /**
   * Get color name by hex value
   */
  static getColorName(hex: string): string | null {
    const color = FAMILY_MEMBER_COLORS.find(
      c => c.hex.toLowerCase() === hex.toLowerCase()
    );
    return color?.name || null;
  }

  /**
   * Get the default color (first in the palette)
   */
  static getDefaultColor(): ColorOption {
    return FAMILY_MEMBER_COLORS[0];
  }

  /**
   * Normalize a color - returns the color if valid hex, or the default color if malformed/empty
   */
  static normalizeColor(hex: string | undefined | null): string {
    if (!hex || !this.isValidColor(hex)) {
      return this.getDefaultColor().hex;
    }
    return hex;
  }
}
